/**
 * Shared ownership utilities — normalization, validation, and constants.
 * Eliminates duplication across IdentityManager, ManageMSI, ApisPage, and ApiForm.
 */

import type {
  ManagedIdentityDisplayRecord,
  OwnerDisplayEntry,
  OwnerResolution,
} from '../types/trapi';

export const GUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export const OWNER_EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/i;

export function isValidGuid(value: string): boolean {
  return GUID_PATTERN.test(value.trim());
}

/**
 * Normalize owner data from various API formats to a sorted, deduplicated string[].
 * Handles: string, string[], undefined, null, nested arrays.
 */
export function normalizeOwners(raw: unknown): string[] {
  const set = new Set<string>();
  if (Array.isArray(raw)) {
    raw.forEach(item => {
      if (typeof item === 'string') {
        const t = item.trim().toLowerCase();
        if (t) set.add(t);
      }
    });
  } else if (typeof raw === 'string') {
    const t = raw.trim().toLowerCase();
    if (t) set.add(t);
  }
  return Array.from(set).sort();
}

// ---- Phase 3 owner display dual-read helpers ----
// The display edge NEVER shows a GUID. Unresolved/pending owners render as fixed,
// non-identifying UI text that is NEVER submitted back as an owner value.

export const OWNER_UNAVAILABLE_LABEL = 'Owner unavailable';
export const OWNER_PENDING_LABEL = 'Owner resolution pending';

// Type guard: Phase-3 display record (carries ownerDisplay.entries[]).
export function isOwnerDisplayRecord(value: unknown): value is ManagedIdentityDisplayRecord {
  if (!value || typeof value !== 'object') return false;
  const od = (value as { ownerDisplay?: unknown }).ownerDisplay;
  if (!od || typeof od !== 'object') return false;
  return Array.isArray((od as { entries?: unknown }).entries);
}

// Type guard: a single owner display entry (no OID exposure).
export function isOwnerDisplayEntry(value: unknown): value is OwnerDisplayEntry {
  if (!value || typeof value !== 'object') return false;
  const e = value as Record<string, unknown>;
  const labelOk = e.label === null || typeof e.label === 'string';
  const resOk = e.resolution === 'resolved' || e.resolution === 'unresolved' || e.resolution === 'pending';
  return labelOk && resOk && typeof e.isPrimary === 'boolean';
}

// Non-identifying display text for an entry. Never returns a GUID.
export function ownerEntryText(entry: OwnerDisplayEntry): string {
  if (entry.resolution === 'resolved' && entry.label) return entry.label;
  if (entry.resolution === 'pending') return OWNER_PENDING_LABEL;
  return OWNER_UNAVAILABLE_LABEL;
}

export interface OwnerDisplayRow {
  text: string;            // non-identifying display text (never a GUID)
  resolution: OwnerResolution;
  isPrimary: boolean;
  editable: boolean;       // true only for resolved UPN labels the user may resubmit
  value: string;           // editable owner value (UPN) or '' when not editable
}

// Dual-read: derive display rows from either the Phase-3 display DTO or the legacy
// Phase-2 OID shape. Legacy GUID owners are hidden as 'Owner unavailable'; the caller
// relies on server-side preserve/merge to retain unresubmitted canonical owners.
export function toOwnerDisplayRows(record: unknown): OwnerDisplayRow[] {
  if (isOwnerDisplayRecord(record)) {
    return record.ownerDisplay.entries.filter(isOwnerDisplayEntry).map(e => ({
      text: ownerEntryText(e),
      resolution: e.resolution,
      isPrimary: e.isPrimary,
      editable: e.resolution === 'resolved' && !!e.label,
      value: e.resolution === 'resolved' && e.label ? e.label : '',
    }));
  }
  const raw = record as { owners?: unknown; owner?: unknown; primaryOwnerId?: unknown };
  const owners = normalizeOwners(raw.owners ?? raw.owner);
  const primary = typeof raw.primaryOwnerId === 'string' ? raw.primaryOwnerId.trim().toLowerCase() : '';
  return owners.map(o => {
    const isGuid = GUID_PATTERN.test(o);
    const resolution: OwnerResolution = isGuid ? 'unresolved' : 'resolved';
    return {
      text: isGuid ? OWNER_UNAVAILABLE_LABEL : o,
      resolution,
      isPrimary: !!primary && o === primary,
      editable: !isGuid,
      value: isGuid ? '' : o,
    };
  });
}

// Editable owner values (resolved UPN labels only) for seeding the edit form.
// Sentinel UI text (Owner unavailable / Owner resolution pending) is NEVER included.
export function editableOwnerValues(record: unknown): string[] {
  return toOwnerDisplayRows(record).filter(r => r.editable).map(r => r.value);
}
